"use client";

import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  mobileNumber: z
    .string()
    .min(11, { message: "Phone must be at least 11 characters" })
    .regex(/^\d+$/, { message: "Phone must contain only numbers" }),
  age: z
    .union([
      z
        .number()
        .min(12, "Age must be at least 12")
        .max(150, "Age must be realistic"),
      z.nan(),
      z.literal(0),
      z.literal(""),
    ])
    .optional(),
  gender: z.enum(["Male", "Female"]).optional(),
});

const defaultValues = {
  name: "",
  mobileNumber: "",
  age: undefined,
  gender: undefined,
};

function WelcomeOffer() {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const router = useRouter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      // Convert age to number or undefined before sending
      const submitData = {
        ...values,
        age: values.age && values.age !== 0 ? Number(values.age) : undefined,
        isQrCode: 1,
        address: "Bogra",
      };

      const {
        data: { error, message, data },
      } = await axios.post(
        `${process.env.NEXT_PUBLIC_POS_URL}/api/save-customer`,
        submitData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!error) {
        toast.success(message);
        router.push(`/success/?id=${data.uhid}`);
      } else {
        toast.error(message);
      }
    } catch (error: unknown) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md mx-auto">
        {/* Header matching the reference image */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 border p-8 rounded-xl bg-none shadow-lg backdrop-blur-3xl"
          >
            <div className="flex flex-col items-center mb-4 gap-4">
              <Image
                src={"/foundation32-logo.png"}
                alt="Foundation32 Logo"
                height={200}
                width={200}
                priority
                unoptimized
              />
              <p className="text-black">Welcome to our exclusive offer</p>
            </div>

            {/* Name field */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-900">
                    Name *
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-800">
                    This is your public display name.
                  </FormDescription>
                  <FormControl>
                    <Input
                      className="bg-white w-full border-gray-300 focus:border-fuchsia-400 focus:ring-fuchsia-400"
                      placeholder="John Doe"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Phone field */}
            <FormField
              control={form.control}
              name="mobileNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-900">
                    Phone *
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-800">
                    This is your Phone Number (11 digits).
                  </FormDescription>
                  <FormControl>
                    <Input
                      className="bg-white w-full border-gray-300 focus:border-fuchsia-400 focus:ring-fuchsia-400"
                      placeholder="ex: 01711111111"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Age field */}
            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-900">
                    Age
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-800">
                    This is your Age.
                  </FormDescription>
                  <FormControl>
                    <Input
                      className="bg-white w-full border-gray-300 focus:border-teal-300 focus:ring-teal-300"
                      placeholder="18"
                      type="number"
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === "" ? undefined : Number(value)
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Gender field */}
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-900">
                    Gender
                  </FormLabel>
                  <FormDescription className="text-xs text-gray-800 mb-2">
                    Select your gender.
                  </FormDescription>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full bg-white border-gray-300 focus:border-fuchsia-400 focus:ring-fuchsia-400">
                        <SelectValue placeholder="Select your gender" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full bg-[#069EDB] hover:bg-[#0287bb]  text-white font-extrabold py-3 rounded-lg transition-all duration-200 transform hover:scale-105 cursor-pointer"
              disabled={isLoading}
            >
              {isLoading ? "Submitting" : "Submit"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default WelcomeOffer;
